// ** React Imports
import React from 'react'
import { CustomizeUIProvider } from 'src/lib/contexts/CustomizeUIProvider'
import { useCustomizeUIContext } from 'src/lib/contexts/CustomizeUIProvider'
import { ExitNudgeProvider, useExitNudge } from 'src/lib/contexts/ExitNudgeContext'
import { DelhiveryPreviewProvider } from 'src/lib/contexts/DelhiveryPreviewContext'
import UIPreview from 'src/components/common/ui-preview/UIPreview'
import { getPreviewConfig } from 'src/lib/utils/customizeUIHelpers'
import { CustomizeUIPagesType } from 'src/lib/types/customizeUI'
import SaveNotification from 'src/components/common/SaveNotification'
import { errorToast } from 'src/lib/utils/toasters'

interface UICustomizationLayoutProps {
  children: React.ReactNode
  title: string
  pageType?: CustomizeUIPagesType
  showPreview?: boolean
}

const UICustomizationLayout: React.FC<UICustomizationLayoutProps> = ({
  children,
  title,
  pageType,
  showPreview = true
}) => {
  return (
    <ExitNudgeProvider>
    <DelhiveryPreviewProvider>
    <CustomizeUIProvider>
      <UICustomizationContent
        title={title}
        pageType={pageType}
        showPreview={showPreview}
      >
        {children}
      </UICustomizationContent>
    </CustomizeUIProvider>
    </DelhiveryPreviewProvider>
    </ExitNudgeProvider>
  )
}

interface UICustomizationContentProps {
  children: React.ReactNode
  title: string
  pageType?: CustomizeUIPagesType
  showPreview?: boolean
}

const UICustomizationContent: React.FC<UICustomizationContentProps> = ({
  children,
  title,
  pageType,
  showPreview
}) => {
  const state = useCustomizeUIContext(context => context.state)
  const isUpdated = useCustomizeUIContext(context => context?.isUpdated)
  const saveChanges = useCustomizeUIContext(context => context?.actions?.saveChanges)
  const discardChanges = useCustomizeUIContext(context => context?.actions?.discardChanges)
  const { isDirty: isExitNudgeDirty, saveExitNudge, discardExitNudge, validateExitNudge, isCouponCreationOpen } = useExitNudge()

  const isAnyDirty = (isUpdated || isExitNudgeDirty) && !isCouponCreationOpen

  const handleSave = async () => {
    const exitNudgeError = validateExitNudge()
    if (exitNudgeError) {
      errorToast(exitNudgeError)
      return
    }
    if (isExitNudgeDirty) await saveExitNudge()
    if (isUpdated) await saveChanges()
  }

  const handleDiscard = () => {
    if (isExitNudgeDirty) discardExitNudge()
    if (isUpdated) discardChanges()
  }

  return (
    <>
      <div className='space-y-4 p-8'>
        <h3 className='text-lg font-semibold text-coal dark pb-2'>{title}</h3>
        <div className='flex w-full'>
          <div className='w-7/12'>
            {children}
          </div>
          {showPreview && pageType && (
            <div className='relative w-5/12'>
              <UIPreview {...getPreviewConfig(pageType, state)} />
            </div>
          )}
        </div>
      </div>
      <SaveNotification isVisible={isAnyDirty} onSave={handleSave} onDiscard={handleDiscard} />
    </>
  )
}

export default UICustomizationLayout 
